/**
 * Ad Creative Builder - Visual ad creation for social media platforms
 * Create display ads, video ads, carousel ads with AI-powered copy
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Select } from '../components/ui/Select';
import { Icon, type IconName } from '../components/icons';
import type { SocialPlatform } from '../types';

type AdType = 'image' | 'video' | 'carousel' | 'story';
type AspectRatio = '1:1' | '16:9' | '9:16' | '4:5' | '1.91:1';

interface AdCreative {
  id: string;
  name: string;
  ad_type: AdType;
  platforms: SocialPlatform[];
  headline: string;
  primary_text: string;
  description: string;
  cta_text: string;
  cta_url: string;
  media_urls: string[];
  aspect_ratio: AspectRatio;
  status: 'draft' | 'review' | 'approved';
}

const AD_TYPES: { value: AdType; label: string; icon: IconName }[] = [
  { value: 'image', label: 'Image Ad', icon: 'image' },
  { value: 'video', label: 'Video Ad', icon: 'video' },
  { value: 'carousel', label: 'Carousel Ad', icon: 'layers' },
  { value: 'story', label: 'Story Ad', icon: 'smartphone' },
];

const ASPECT_RATIOS: { value: AspectRatio; label: string; description: string }[] = [
  { value: '1:1', label: 'Square (1:1)', description: 'Feed posts' },
  { value: '16:9', label: 'Landscape (16:9)', description: 'YouTube, Twitter' },
  { value: '9:16', label: 'Portrait (9:16)', description: 'Stories, Reels, TikTok' },
  { value: '4:5', label: 'Portrait (4:5)', description: 'Instagram Feed' },
  { value: '1.91:1', label: 'Wide (1.91:1)', description: 'Facebook Link Ads' },
];

const CTA_OPTIONS = [
  'Shop Now',
  'Learn More',
  'Sign Up',
  'Get Started',
  'Book Now',
  'Contact Us',
  'Download',
  'Subscribe',
  'Get Offer',
  'Watch More',
];

const PLATFORM_OPTIONS: SocialPlatform[] = ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok', 'youtube'];

export function AdCreativeBuilder() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [adType, setAdType] = useState<AdType>('image');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['instagram', 'facebook']);
  const [headline, setHeadline] = useState('');
  const [primaryText, setPrimaryText] = useState('');
  const [description, setDescription] = useState('');
  const [ctaText, setCtaText] = useState('Learn More');
  const [ctaUrl, setCtaUrl] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [mediaUrls, setMediaUrls] = useState<string[]>(['']);

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [savedAds, setSavedAds] = useState<AdCreative[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (selectedBrand) {
      fetchSavedAds();
    }
  }, [selectedBrand]);

  const fetchSavedAds = async () => {
    // TODO: Fetch from API when endpoint is ready
  };

  const handleGenerateCopy = async () => {
    if (!selectedBrand) return;

    setIsGenerating(true);
    setError('');

    try {
      const response = await apiClient.generateCaption({
        brand_id: selectedBrand.id,
        prompt: `Create ad copy for a ${adType} advertisement. The ad should be compelling and drive action. Platform: ${platforms.join(', ')}. ${name ? `Ad name/theme: ${name}` : ''}`,
        platform: platforms[0] || 'instagram',
        tone: 'professional',
        length: 'medium',
        include_hashtags: false,
        include_emoji: true,
      });

      if (response.success && response.data) {
        const content = response.data.caption;

        // Parse the generated content into ad components
        const lines = content.split('\n').filter((l: string) => l.trim());
        if (lines.length >= 1) setHeadline(lines[0].substring(0, 40));
        if (lines.length >= 2) setPrimaryText(lines.slice(1).join(' ').substring(0, 125));
        if (lines.length >= 3) setDescription(lines[lines.length - 1].substring(0, 30));

        setSuccess('Ad copy generated! Feel free to customize.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to generate ad copy');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!selectedBrand || !name.trim()) {
      setError('Please enter an ad name');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // TODO: Save to API when endpoint is ready
      const newAd: AdCreative = {
        id: `ad-${Date.now()}`,
        name,
        ad_type: adType,
        platforms,
        headline,
        primary_text: primaryText,
        description,
        cta_text: ctaText,
        cta_url: ctaUrl,
        media_urls: mediaUrls.filter(u => u.trim()),
        aspect_ratio: aspectRatio,
        status: 'draft',
      };

      setSavedAds([newAd, ...savedAds]);
      setSuccess('Ad creative saved as draft!');
      setTimeout(() => setSuccess(''), 3000);

      // Reset form
      setName('');
      setHeadline('');
      setPrimaryText('');
      setDescription('');
    } catch (err) {
      setError('Failed to save ad creative');
    } finally {
      setIsSaving(false);
    }
  };

  const togglePlatform = (platform: SocialPlatform) => {
    setPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const addMediaSlot = () => {
    if (mediaUrls.length < 10) {
      setMediaUrls([...mediaUrls, '']);
    }
  };

  const removeMediaSlot = (index: number) => {
    setMediaUrls(mediaUrls.filter((_, i) => i !== index));
  };

  const updateMediaUrl = (index: number, url: string) => {
    const newUrls = [...mediaUrls];
    newUrls[index] = url;
    setMediaUrls(newUrls);
  };

  // Get aspect ratio dimensions for preview
  const getPreviewDimensions = (ratio: AspectRatio) => {
    const widths: Record<AspectRatio, number> = {
      '1:1': 300,
      '16:9': 400,
      '9:16': 225,
      '4:5': 280,
      '1.91:1': 382,
    };
    const heights: Record<AspectRatio, number> = {
      '1:1': 300,
      '16:9': 225,
      '9:16': 400,
      '4:5': 350,
      '1.91:1': 200,
    };
    return { width: widths[ratio], height: heights[ratio] };
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-6">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const previewDimensions = getPreviewDimensions(aspectRatio);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Ad Creative Builder</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Create stunning ads for{' '}
            <span className="font-medium" style={{ color: selectedBrand.color_primary }}>
              {selectedBrand.name}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Icon name="eye" size="sm" className="mr-2" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ad Type Selection */}
          <Card>
            <CardHeader title="Ad Type" subtitle="Choose the format for your advertisement" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {AD_TYPES.map(({ value, label, icon }) => (
                <button
                  key={value}
                  onClick={() => setAdType(value)}
                  className={`p-4 rounded-lg border-2 text-center transition-all ${
                    adType === value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <Icon name={icon} size="lg" className="mx-auto mb-2" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Platforms */}
          <Card>
            <CardHeader title="Target Platforms" subtitle="Select where this ad will run" />
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map(platform => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`px-4 py-2 rounded-lg border-2 capitalize transition-all ${
                    platforms.includes(platform)
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </Card>

          {/* Ad Copy */}
          <Card>
            <CardHeader
              title="Ad Copy"
              subtitle="Write compelling copy or generate with AI"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleGenerateCopy}
                  isLoading={isGenerating}
                >
                  <Icon name="sparkles" size="sm" className="mr-1" />
                  Generate
                </Button>
              }
            />

            <div className="space-y-4">
              <Input
                label="Ad Name"
                placeholder="e.g., Summer Sale Campaign"
                value={name}
                onChange={e => setName(e.target.value)}
              />

              <Input
                label="Headline"
                placeholder="Grab attention with a bold headline"
                value={headline}
                onChange={e => setHeadline(e.target.value)}
                maxLength={40}
              />
              <p className="text-xs text-gray-500 -mt-2">{headline.length}/40 characters</p>

              <Textarea
                label="Primary Text"
                placeholder="Main message of your ad"
                value={primaryText}
                onChange={e => setPrimaryText(e.target.value)}
                rows={3}
                maxLength={125}
              />
              <p className="text-xs text-gray-500 -mt-2">{primaryText.length}/125 characters</p>

              <Input
                label="Description (optional)"
                placeholder="Additional details"
                value={description}
                onChange={e => setDescription(e.target.value)}
                maxLength={30}
              />
            </div>
          </Card>

          {/* Call to Action */}
          <Card>
            <CardHeader title="Call to Action" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="CTA Button"
                options={CTA_OPTIONS.map(cta => ({ value: cta, label: cta }))}
                value={ctaText}
                onChange={e => setCtaText(e.target.value)}
              />
              <Input
                label="Destination URL"
                placeholder="https://your-website.com/landing"
                value={ctaUrl}
                onChange={e => setCtaUrl(e.target.value)}
              />
            </div>
          </Card>

          {/* Media */}
          <Card>
            <CardHeader
              title="Media Assets"
              subtitle={adType === 'carousel' ? 'Add multiple images for carousel' : 'Add your ad image or video'}
              action={
                adType === 'carousel' && (
                  <Button size="sm" variant="outline" onClick={addMediaSlot}>
                    <Icon name="plus" size="sm" className="mr-1" />
                    Add Slide
                  </Button>
                )
              }
            />

            {/* Aspect Ratio */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
              <div className="flex flex-wrap gap-2">
                {ASPECT_RATIOS.map(({ value, label, description }) => (
                  <button
                    key={value}
                    onClick={() => setAspectRatio(value)}
                    className={`px-3 py-2 rounded-lg border-2 text-left transition-all ${
                      aspectRatio === value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className={`text-sm font-medium ${aspectRatio === value ? 'text-blue-700' : 'text-gray-700'}`}>
                      {label}
                    </span>
                    <span className="block text-xs text-gray-500">{description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Media URLs */}
            <div className="space-y-3">
              {mediaUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder={`${adType === 'video' ? 'Video' : 'Image'} URL ${adType === 'carousel' ? `#${index + 1}` : ''}`}
                    value={url}
                    onChange={e => updateMediaUrl(index, e.target.value)}
                    className="flex-1"
                  />
                  {mediaUrls.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMediaSlot(index)}
                    >
                      <Icon name="trash" size="sm" className="text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={() => navigate('/posts')} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleSave}
              isLoading={isSaving}
              className="w-full sm:w-auto"
            >
              <Icon name="save" size="sm" className="mr-2" />
              Save Draft
            </Button>
            <Button className="w-full sm:w-auto">
              <Icon name="download" size="sm" className="mr-2" />
              Export Ad
            </Button>
          </div>
        </div>

        {/* Right Column - Preview */}
        {showPreview && (
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader title="Ad Preview" subtitle={`${aspectRatio} format`} />

              <div className="flex justify-center mb-4">
                <div
                  className="bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center"
                  style={{
                    width: previewDimensions.width,
                    height: previewDimensions.height,
                  }}
                >
                  {mediaUrls[0] ? (
                    <img
                      src={mediaUrls[0]}
                      alt="Ad preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="text-center text-gray-400">
                      <Icon name="image" size="3xl" className="mx-auto mb-2" />
                      <p className="text-sm">Add media to preview</p>
                    </div>
                  )}

                  {/* Overlay with ad copy */}
                  {(headline || primaryText) && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
                      {headline && (
                        <h3 className="text-white font-bold text-lg leading-tight mb-1">
                          {headline}
                        </h3>
                      )}
                      {primaryText && (
                        <p className="text-white/90 text-sm line-clamp-2">
                          {primaryText}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* CTA Preview */}
              {ctaText && (
                <div className="flex justify-center">
                  <button className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">
                    {ctaText}
                  </button>
                </div>
              )}

              {/* Platform indicators */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Optimized for:</p>
                <div className="flex flex-wrap gap-1">
                  {platforms.map(p => (
                    <span
                      key={p}
                      className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 capitalize"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Saved Ads */}
      {savedAds.length > 0 && (
        <Card>
          <CardHeader title="Saved Ad Creatives" subtitle={`${savedAds.length} drafts`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedAds.map(ad => (
              <div
                key={ad.id}
                className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{ad.name}</h4>
                    <p className="text-xs text-gray-500 capitalize">{ad.ad_type} Ad</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    ad.status === 'approved' ? 'bg-green-100 text-green-800' :
                    ad.status === 'review' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {ad.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{ad.headline}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1">
                    <Icon name="copy" size="xs" className="mr-1" />
                    Duplicate
                  </Button>
                  <Button size="sm" variant="ghost">
                    <Icon name="trash" size="xs" className="text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
